import { useCallback, useRef, useState } from 'react';

import { exportApi } from '../../api/export.js';
import { rulesApi } from '../../api/rules.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Permission } from '../../auth/roles.js';
import {
  Card, EmptyState, ErrorState, ExportCsvButton, LoadingState, MessageBanner,
} from '../../components/ui';
import { useInfiniteScroll } from '../../hooks';
import { useUrlColumns, useUrlDebouncedParam, useUrlParam } from '../../url';
import { ColumnPicker } from './ColumnPicker.jsx';
import { EDITABLE_COLS, PAY_CODE_ABBR_MAP, PAY_CODE_COL_WIDTHS, PAY_CODE_DEFAULT_COLS } from './constants.js';
import { EditableCell } from './EditableCell.jsx';
import { formatValue } from './helpers.jsx';
import { PayCodeAddForm } from './PayCodeAddForm.jsx';

// ``urlActive`` — this is the visible Rules sub-tab, so its filters own the
// query string. See DataExplorer for why the hidden explorers must not.
export function PayCodeExplorer({ urlActive = true }) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.EDIT_RULES);

  const urlOpts = { active: urlActive };
  const [selectedCols,  setSelectedCols]  = useUrlColumns('cols', PAY_CODE_DEFAULT_COLS, urlOpts);
  const [pickerOpen,    setPickerOpen]    = useState(false);
  const [search, setSearch, debouncedSearch] = useUrlDebouncedParam('q', urlOpts);
  const [filterBilling, setFilterBilling] = useUrlParam('billing', 'all', urlOpts);
  const [msg,      setMsg]      = useState(null);
  const [saving,   setSaving]   = useState(null);
  const [adding,   setAdding]   = useState(false);
  const [selected, setSelected] = useState(new Set());

  // The table scrolls inside its own box so the header row can stay frozen —
  // that box is also the infinite-scroll sentinel's root.
  const scrollRef = useRef(null);

  // One filter builder for the table and the CSV — see DataExplorer.
  const queryParams = useCallback(
    () => ({ search: debouncedSearch, filter_billing: filterBilling }),
    [debouncedSearch, filterBilling],
  );

  const fetchPage = useCallback(
    (offset, limit) => rulesApi.listPayCodes({ ...queryParams(), limit, offset }),
    [queryParams],
  );

  const {
    items, summary, columns: rawColumns, total,
    loading, loadingMore, hasMore, error, sentinelRef, updateItem, removeItems, reset,
  } = useInfiniteScroll(fetchPage, [fetchPage], { rootRef: scrollRef });

  const allColumns = rawColumns.filter((c) => !c.editable);
  const activeCols = allColumns.filter((c) => selectedCols.has(c.key));

  const toggleCol = (key) => {
    const next = new Set(selectedCols);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelectedCols(next);
  };

  const selectTable = (table) => {
    const tableKeys = allColumns.filter((c) => c.table === table).map((c) => c.key);
    const allSelected = tableKeys.every((k) => selectedCols.has(k));
    const next = new Set(selectedCols);
    tableKeys.forEach((k) => (allSelected ? next.delete(k) : next.add(k)));
    setSelectedCols(next);
  };

  const handleFieldChange = async (row, colKey, newValue) => {
    setSaving(`${row.id}_${colKey}`);
    try {
      await rulesApi.patchPayCodeField(row.id, colKey, newValue != null ? String(newValue) : null);
      updateItem('id', row.id, { [colKey]: newValue });
    } catch (e) {
      setMsg({ type: 'error', text: `Save failed: ${e.message}` });
    }
    setSaving(null);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (
      prev.size === items.length && items.length > 0
        ? new Set()
        : new Set(items.map((r) => r.id))
    ));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} pay code rule${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setSaving('bulk_delete');
    try {
      const ids = [...selected];
      const res = await rulesApi.bulkDeletePayCodes(ids);
      removeItems('id', ids);
      setSelected(new Set());
      setMsg({ type: 'success', text: `Deleted ${res.deleted} rule${res.deleted === 1 ? '' : 's'}` });
    } catch (e) {
      setMsg({ type: 'error', text: `Delete failed: ${e.message}` });
    }
    setSaving(null);
  };

  return (
    <>
      {summary && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <Card label="Total Rules" value={summary.total} />
          <Card label="Billable"   value={summary.billable}   color="#155724" />
          <Card label="Excluded"   value={summary.excluded}   color="#dc3545" />
          <Card label="Categories" value={summary.categories} color="#0d47a1" />
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => setPickerOpen(!pickerOpen)}
          style={{ marginBottom: 8 }}
        >
          {pickerOpen ? 'Hide Columns' : 'Choose Columns'} ({activeCols.length} selected)
        </button>
        {pickerOpen && (
          <ColumnPicker
            columns={allColumns}
            selectedCols={selectedCols}
            onToggle={toggleCol}
            onSelectTable={selectTable}
            abbrMap={PAY_CODE_ABBR_MAP}
            extraEditableNote={
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <strong style={{ fontSize: 12, letterSpacing: 0.3, color: '#333' }}>
                    Editable Columns
                  </strong>
                  <span style={{ fontSize: 10, color: '#999' }}>
                    (always visible, frozen right)
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                  {EDITABLE_COLS.map((ec) => (
                    <span key={ec.key} style={{ fontSize: 12, color: '#666', padding: '2px 0' }}>
                      {ec.fullLabel} ({ec.abbr})
                    </span>
                  ))}
                </div>
              </div>
            }
          />
        )}
      </div>

      <div style={{
        display: 'flex', gap: 12, marginBottom: 16,
        flexWrap: 'wrap', alignItems: 'center',
      }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Search</label>
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..." style={{ width: 200 }}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: 11 }}>Billing Status</label>
          <select value={filterBilling} onChange={(e) => setFilterBilling(e.target.value)}>
            <option value="all">All</option>
            <option value="billable">Billable</option>
            <option value="excluded">Excluded</option>
          </select>
        </div>
        {canEdit && (
          <button
            className="btn btn-primary"
            onClick={() => setAdding(true)}
            disabled={adding}
            style={{ alignSelf: 'flex-end' }}
          >
            Add Rule
          </button>
        )}
        {canEdit && selected.size > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', alignSelf: 'flex-end', paddingBottom: 4 }}>
            <span style={{ fontSize: 13, color: '#555' }}>{selected.size} selected</span>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleBulkDelete}
              disabled={saving === 'bulk_delete'}
            >
              {saving === 'bulk_delete' ? 'Deleting...' : `Delete ${selected.size}`}
            </button>
          </div>
        )}
        <ExportCsvButton
          onExport={() => exportApi.download('pay_codes', {
            params: queryParams(),
            // Rule # + the chosen columns + the frozen editable ones.
            columns: ['id', ...activeCols.map((c) => c.key),
                      ...EDITABLE_COLS.map((c) => c.key)],
          })}
          disabled={loading || total === 0 || activeCols.length === 0}
          title="Download every rule matching these filters as CSV — not just the rows loaded here"
          onError={(text) => setMsg({ type: 'error', text })}
          style={{ alignSelf: 'flex-end', paddingBottom: 2 }}
        />
        <div style={{ fontSize: 12, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
          Showing {items.length} of {total}
        </div>
      </div>

      {adding && (
        <PayCodeAddForm
          onSave={async (data) => {
            try {
              await rulesApi.createPayCode(data);
              setMsg({ type: 'success', text: `Created "${data.pay_code_name}"` });
              setAdding(false);
              reset();
            } catch (e) {
              setMsg({ type: 'error', text: e.message });
            }
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <MessageBanner msg={msg} onDismiss={() => setMsg(null)} />
      {error && <ErrorState>{error}</ErrorState>}

      {loading
        ? <LoadingState>Loading pay code rules...</LoadingState>
        : activeCols.length === 0
          ? <EmptyState>No columns selected. Click "Choose Columns" to pick which data to display.</EmptyState>
          : (
            <div className="table-scroll" ref={scrollRef}>
              <table style={{ width: '100%', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '3%', textAlign: 'center' }}>
                      {canEdit && (
                        <input
                          type="checkbox" className="cb"
                          onChange={toggleSelectAll}
                          checked={selected.size === items.length && items.length > 0}
                        />
                      )}
                    </th>
                    <th style={{ whiteSpace: 'nowrap', width: '3%', textAlign: 'center' }}>Rule #</th>
                    {activeCols.map((col) => {
                      const w = PAY_CODE_COL_WIDTHS[col.key];
                      return (
                        <th key={col.key} style={{
                          fontSize: 11, whiteSpace: 'nowrap',
                          ...(w ? { width: w } : {}),
                        }}>
                          <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: '#999' }}>
                            {col.table || col.group}
                          </span>
                          {PAY_CODE_ABBR_MAP[col.label] || col.label}
                        </th>
                      );
                    })}
                    {EDITABLE_COLS.map((ec) => (
                      <th key={ec.key} style={{
                        fontSize: 11, whiteSpace: 'nowrap',
                        width: ec.width, textAlign: 'center',
                      }}>
                        <span style={{ display: 'block', fontSize: 9, fontWeight: 400, color: '#90caf9' }}>
                          {ec.group}
                        </span>
                        {ec.abbr}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} style={{ opacity: r.ignore_for_billing ? 0.5 : 1 }}>
                      <td style={{ width: '3%', textAlign: 'center' }}>
                        {canEdit && (
                          <input
                            type="checkbox" className="cb"
                            checked={selected.has(r.id)}
                            onChange={() => toggleSelect(r.id)}
                          />
                        )}
                      </td>
                      <td style={{ width: '3%', textAlign: 'center' }}><strong>{r.id}</strong></td>
                      {activeCols.map((col) => {
                        const w = PAY_CODE_COL_WIDTHS[col.key];
                        return (
                          <td key={col.key} style={{
                            fontSize: 12, whiteSpace: 'nowrap',
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            ...(w ? { width: w } : {}),
                          }}>
                            {formatValue(r[col.key], col.key)}
                          </td>
                        );
                      })}
                      {EDITABLE_COLS.map((ec) => (
                        <td key={ec.key} style={{
                          textAlign: 'center', padding: '4px 6px',
                          width: ec.width, overflow: 'hidden',
                        }}>
                          <EditableCell
                            row={r} ec={ec}
                            saving={saving} setSaving={setSaving}
                            onChange={handleFieldChange}
                            readonly={!canEdit}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ textAlign: 'center', padding: 12, color: '#666', fontSize: 13 }}>
                  Loading more...
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <div style={{ textAlign: 'center', padding: 12, color: '#999', fontSize: 12 }}>
                  All {total} rules loaded
                </div>
              )}
            </div>
          )
      }
    </>
  );
}
